import React, { useState, useRef, useEffect, useCallback } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { User, ChevronDown, ChevronUp, LogOut, Settings, Package, BookOpen } from "lucide-react";
import { navigationLinks, otherLinks } from "@/constants";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuthorRedirect } from '@/hooks/useAuthorRedirect';
import { useLogoutDialog } from '../Layout';

interface NavbarDesktopProps {
  isDarkTheme: boolean;
  isHomePage: boolean;
}

// 定义导航项接口
interface NavItem {
  name: string;
  path: string;
  dropdown?: boolean;
  items?: NavItemChild[];
  external?: boolean;
  customRedirect?: string;
}

// 定义子导航项接口
interface NavItemChild {
  name: string;
  path: string;
  external?: boolean;
  customRedirect?: string;
}

// 创建持久化的头像组件
const PersistentAvatar = React.memo(({ user }: { user: any }) => {
  return (
    <div className="w-8 h-8 relative">
      <div className="absolute inset-0 rounded-full overflow-hidden">
        {user?.avatar ? (
          <img
            src={user.avatar}
            alt={user.name}
            className="w-full h-full object-cover will-change-transform"
            style={{
              WebkitPerspective: '1000px',
              WebkitBackfaceVisibility: 'hidden',
              WebkitTransform: 'translate3d(0, 0, 0)',
              WebkitTransformStyle: 'preserve-3d'
            }}
          />
        ) : (
          <div className="w-full h-full bg-anzir/20 text-anzir flex items-center justify-center text-xs">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
});

const NavbarDesktop = ({ isDarkTheme, isHomePage }: NavbarDesktopProps) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { goToAuthorPlatform } = useAuthorRedirect();
  const { openLogoutDialog } = useLogoutDialog();

  useEffect(() => {
    const closeDropdown = (e: MouseEvent) => {
      setOpenDropdown(null);
    };

    if (openDropdown) {
      document.addEventListener('click', closeDropdown);
    }

    return () => {
      document.removeEventListener('click', closeDropdown);
    };
  }, [openDropdown]);

  const toggleDropdown = (name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (openDropdown === name) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(name);
    }
  };

  // 阻止下拉菜单内的点击事件冒泡
  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Check if current path is a child of a dropdown menu item
  const isChildActive = (items: { path: string }[] | undefined) => {
    if (!items) return false;
    return items.some(item => location.pathname === item.path);
  };

  // Helper function to get translation key for navigation items
  const getNavTranslationKey = (name: string) => {
    // Convert the name to lowercase and handle special cases
    const key = name.toLowerCase().replace(/\s+/g, '');

    // If it's a direct match to an existing key in the translation files, use it
    if (t(`nav.${key}`) !== key) {
      return `nav.${key}`;
    }

    // Otherwise, match to the closest key
    switch (key) {
      case 'home':
        return 'nav.home';
      case 'library':
        return 'nav.library';
      case 'player':
        return 'nav.player';
      case 'storeowner':
        return 'nav.storeowner';
      case 'author':
        return 'nav.author';
      case 'helpcenter':
        return 'nav.help';
      case 'community':
        return 'nav.community';
      case 'blog':
        return 'nav.blog';
      case 'aboutus':
        return 'nav.about';
      case 'careers':
        return 'nav.careers';
      case 'other':
        return 'nav.more';
      default:
        return `nav.${key}`;
    }
  };

  const handleCustomRedirect = (type: string) => {
    if (type === 'author') {
      setOpenDropdown(null);
      goToAuthorPlatform();
    }
  };

  return (
    <div className="hidden md:flex items-center gap-6" ref={dropdownRef}>
      <nav className="flex gap-8">
        {navigationLinks.map((link: NavItem) => (
          <div key={link.name} className="relative">
            {link.dropdown ? (
              <>
                <button
                  onClick={(e) => toggleDropdown(link.name, e)}
                  className={`flex items-center gap-1 ${
                    isChildActive(link.items)
                      ? `${isDarkTheme ? "text-white" : "text-gray-900"} font-medium border-b-2 border-[#F97316] pb-1`
                      : isDarkTheme 
                        ? "text-white/80 hover:text-white" 
                        : "text-gray-700 hover:text-gray-900"
                  } transition-colors`}
                >
                  {t(getNavTranslationKey(link.name))}
                  {openDropdown === link.name ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>

                {openDropdown === link.name && link.items && (
                  <div 
                    onClick={handleDropdownClick}
                    className={`absolute top-full left-0 mt-2 w-48 rounded-md shadow-lg py-1 z-20 ${
                      isDarkTheme ? "bg-black/80 backdrop-blur-sm border border-white/10" : "bg-white"
                    }`}
                  >
                    {link.items.map((item: NavItemChild) => (
                      <div key={item.name} className="block px-4 py-2">
                        {item.customRedirect ? (
                          <button
                            className={`text-sm ${isDarkTheme ? "text-white/80 hover:text-white" : "text-gray-700 hover:text-gray-900"} transition-colors w-full text-left`}
                            onClick={() => handleCustomRedirect(item.customRedirect)}
                          >
                            {t(getNavTranslationKey(item.name))}
                          </button>
                        ) : item.external ? (
                          <a
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-sm ${isDarkTheme ? "text-white/80 hover:text-white" : "text-gray-700 hover:text-gray-900"} transition-colors`}
                          >
                            {t(getNavTranslationKey(item.name))}
                          </a>
                        ) : (
                          <NavLink
                            to={item.path}
                            className={({ isActive }) =>
                              `text-sm ${
                                isActive
                                  ? isDarkTheme 
                                    ? "text-white font-medium" 
                                    : "text-gray-900 font-medium"
                                  : isDarkTheme
                                    ? "text-white/80 hover:text-white" 
                                    : "text-gray-700 hover:text-gray-900"
                              } transition-colors`
                            }
                            onClick={() => setOpenDropdown(null)}
                          >
                            {t(getNavTranslationKey(item.name))}
                          </NavLink>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div>
                {link.customRedirect ? (
                  <button
                    className={`${isDarkTheme ? "text-white/80 hover:text-white" : "text-gray-700 hover:text-gray-900"} transition-colors`}
                    onClick={() => handleCustomRedirect(link.customRedirect)}
                  >
                    {t(getNavTranslationKey(link.name))}
                  </button>
                ) : link.external ? (
                  <a
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${isDarkTheme ? "text-white/80 hover:text-white" : "text-gray-700 hover:text-gray-900"} transition-colors`}
                  >
                    {t(getNavTranslationKey(link.name))}
                  </a>
                ) : (
                  <NavLink
                    to={link.path}
                    className={({ isActive }) => {
                      const active = link.path === "/"
                        ? isHomePage
                        : isActive || (link.path === "/library" && location.pathname.startsWith("/script/"));
                      return active
                        ? `${isDarkTheme ? "text-white" : "text-gray-900"} font-medium border-b-2 border-[#F97316] pb-1`
                        : `${isDarkTheme ? "text-white/80 hover:text-white" : "text-gray-700 hover:text-gray-900"} transition-colors`;
                    }}
                  >
                    {t(getNavTranslationKey(link.name))}
                  </NavLink>
                )}
              </div>
            )}
          </div>
        ))}

        <div className="relative">
          <button
            onClick={(e) => toggleDropdown("Other", e)}
            className={`flex items-center gap-1 ${
              otherLinks.some(link => location.pathname === link.path)
                ? `${isDarkTheme ? "text-white" : "text-gray-900"} font-medium border-b-2 border-[#F97316] pb-1`
                : isDarkTheme 
                  ? "text-white/80 hover:text-white" 
                  : "text-gray-700 hover:text-gray-900"
            } transition-colors`}
          >
            {t('nav.more')}
            {openDropdown === "Other" ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </button>

          {openDropdown === "Other" && (
            <div 
              onClick={handleDropdownClick}
              className={`absolute top-full left-0 mt-2 w-48 rounded-md shadow-lg py-1 z-20 ${
                isDarkTheme ? "bg-black/80 backdrop-blur-sm border border-white/10" : "bg-white"
              }`}
            >
              {otherLinks.map((link: NavItem) => (
                <NavLink
                  key={link.name}
                  to={link.path}
                  className={({ isActive }) =>
                    `block px-4 py-2 text-sm ${
                      isActive
                        ? isDarkTheme 
                          ? "bg-anzir/20 text-white" 
                          : "bg-gray-100 text-gray-900"
                        : isDarkTheme
                          ? "text-white/80 hover:text-white hover:bg-white/10" 
                          : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    }`
                  }
                  onClick={() => setOpenDropdown(null)}
                >
                  {t(getNavTranslationKey(link.name))}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </nav>

      <div className="flex items-center gap-4">
        <LanguageSwitcher isDarkTheme={isDarkTheme} />

        {isAuthenticated ? (
          <div className="relative">
            <button
              onClick={(e) => toggleDropdown("UserMenu", e)}
              className="flex items-center gap-2 select-none"
            >
              <PersistentAvatar user={user} />
              {openDropdown === "UserMenu" ? (
                <ChevronUp size={16} className={isDarkTheme ? "text-white" : "text-gray-900"} />
              ) : (
                <ChevronDown size={16} className={isDarkTheme ? "text-white" : "text-gray-900"} />
              )}
            </button>

            {openDropdown === "UserMenu" && (
              <div 
                onClick={handleDropdownClick}
                className={`absolute top-full right-0 mt-2 w-48 rounded-md shadow-lg py-1 z-20 ${
                  isDarkTheme ? "bg-black/80 backdrop-blur-sm border border-white/10" : "bg-white"
                }`}
              >
                <div className="px-4 py-2 border-b border-gray-700">
                  <p className={`text-sm font-medium ${isDarkTheme ? "text-white" : "text-gray-900"}`}>{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>

                <Link
                  to="/profile"
                  className={`block px-4 py-2 text-sm ${isDarkTheme ? "text-white/80 hover:text-white hover:bg-white/10" : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"} flex items-center`}
                  onClick={() => setOpenDropdown(null)}
                >
                  <User size={16} className="mr-2" />
                  Profile
                </Link>

                <Link
                  to="/profile/games"
                  className={`block px-4 py-2 text-sm ${isDarkTheme ? "text-white/80 hover:text-white hover:bg-white/10" : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"} flex items-center`}
                  onClick={() => setOpenDropdown(null)}
                >
                  <BookOpen size={16} className="mr-2" />
                  My Games
                </Link>

                <Link
                  to="/profile/orders"
                  className={`block px-4 py-2 text-sm ${isDarkTheme ? "text-white/80 hover:text-white hover:bg-white/10" : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"} flex items-center`}
                  onClick={() => setOpenDropdown(null)}
                >
                  <Package size={16} className="mr-2" />
                  My Orders
                </Link>

                <Link
                  to="/profile/settings"
                  className={`block px-4 py-2 text-sm ${isDarkTheme ? "text-white/80 hover:text-white hover:bg-white/10" : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"} flex items-center`}
                  onClick={() => setOpenDropdown(null)}
                >
                  <Settings size={16} className="mr-2" />
                  Settings
                </Link>

                <button
                  onClick={() => {
                    setOpenDropdown(null);
                    openLogoutDialog();
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm text-red-500 hover:text-red-400 hover:bg-red-900/20 flex items-center`}
                >
                  <LogOut size={16} className="mr-2" />
                  Logout
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link
            to="/login"
            className={`flex items-center gap-2 ${
              isDarkTheme 
                ? "text-white/80 hover:text-white transition-colors" 
                : "text-gray-700 hover:text-gray-900 transition-colors"
            }`}
          >
            <User size={18} />
            <span className="inline">{t('nav.login')}</span>
          </Link>
        )}
      </div>
    </div>
  );
};

// 使用 memo 包装整个组件
export default React.memo(NavbarDesktop);
